/* eslint-disable react/prop-types */
import React from "react";
import "../../assets/styles/App.css";
import { Button, FormGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import { exportExamsToCSV } from "../../constants/endpoints";
import { postAuthenticatedData } from "../../utils/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { withTranslation } from "react-i18next";
import MultiSelect from "react-multi-select-component";

/**
 * Modal component for CSV Export functionality
 * Allows users to select date range and sites for exporting exam data to CSV
 *
 * @component
 */
class CustomCSVExportModal extends React.Component {
  constructor(props) {
    super(props);

    this.t = props.t;
    this.i18n = props.i18n;

    // Initialize options from sites prop
    const options =
      props.sites && props.sites.length > 0
        ? props.sites.map(site => ({
            label: site.siteName,
            value: site.siteId
          }))
        : [];

    // Initialize selected sites from props (default to all available sites or primary site)
    const defaultSelected =
      props.selectedSites && props.selectedSites.length > 0
        ? props.selectedSites
        : options.length > 0
        ? [options[0]]
        : [];

    this.state = {
      startDate: null,
      endDate: null,
      selected: defaultSelected,
      isSubmitting: false
    };

    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.setSelected = this.setSelected.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleStartDateChange(date) {
    this.setState({ startDate: date });
  }

  handleEndDateChange(date) {
    this.setState({ endDate: date });
  }

  setSelected(selected) {
    this.setState({ selected });
  }

  /**
   * Converts a date to midnight (start of day) in milliseconds epoch
   */
  getStartOfDayEpoch(date) {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  /**
   * Converts a date to 11:59:59 PM (end of day) in milliseconds epoch
   */
  getEndOfDayEpoch(date) {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }

  onSubmit() {
    const { startDate, endDate, selected } = this.state;

    // Validation
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (selected.length === 0) {
      toast.error("Please select at least one site");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    this.setState({ isSubmitting: true });

    const siteIds = selected.map(item => item.value);
    const startDateEpoch = this.getStartOfDayEpoch(startDate);
    const endDateEpoch = this.getEndOfDayEpoch(endDate);

    const data = {
      siteIds,
      startDate: startDateEpoch,
      endDate: endDateEpoch
    };

    postAuthenticatedData(
      sessionStorage.getItem("domain") + exportExamsToCSV,
      data
    )
      .then(response => {
        if (response.status === 200) {
          toast.success("Your report will be emailed to you shortly");
          this.props.closePopup();
        } else {
          return response.json().then(resp => {
            if (
              resp.status &&
              resp.status.statusCode !== "OK" &&
              resp.status.statusCode !== "FORBIDDEN" &&
              resp.status.statusCode !== "NOT_AUTHORIZED"
            ) {
              toast.error(resp.status.message || "An error occurred");
            } else {
              toast.error("An error occurred while exporting the CSV");
            }
            this.setState({ isSubmitting: false });
          });
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error("Error exporting CSV:", error);
        toast.error("An error occurred while exporting the CSV");
        this.setState({ isSubmitting: false });
      });
  }

  handleSubmit(event) {
    event.preventDefault();
  }

  render() {
    const { startDate, endDate, selected, isSubmitting } = this.state;
    const options =
      this.props.sites && this.props.sites.length > 0
        ? this.props.sites.map(site => ({
            label: site.siteName,
            value: site.siteId
          }))
        : [];

    return (
      <div className="custommodal">
        <div className="custommodalinner custommodalinnerdatepicker">
          <form
            className="loginpad custom-modal-add-patient"
            onSubmit={this.handleSubmit}
          >
            <h6 className="modal-heading">CSV Export</h6>
            <Button
              onClick={this.props.closePopup}
              className="cancle"
              disabled={isSubmitting}
            >
              {" "}
              x{" "}
            </Button>
            <hr className="hrpadding" />
            <div className="firstpage">
              <FormGroup controlId="startDate" bsSize="large">
                <div className="row">
                  <div className="col-md-3 formfont">Start Date</div>
                  <div className="col-md-9">
                    <div className="customDatePicker">
                      <DatePicker
                        selected={startDate}
                        onChange={this.handleStartDateChange}
                        dateFormat="dd/MM/yyyy"
                        maxDate={new Date()}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="Select start date"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </FormGroup>

              <FormGroup controlId="endDate" bsSize="large">
                <div className="row">
                  <div className="col-md-3 formfont">End Date</div>
                  <div className="col-md-9">
                    <div className="customDatePicker">
                      <DatePicker
                        selected={endDate}
                        onChange={this.handleEndDateChange}
                        dateFormat="dd/MM/yyyy"
                        minDate={startDate}
                        maxDate={new Date()}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="Select end date"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </FormGroup>

              <FormGroup controlId="sites" bsSize="large">
                <div className="row">
                  <div className="col-md-3 formfont">Site</div>
                  <div className="col-md-9">
                    <MultiSelect
                      options={options}
                      value={selected}
                      onChange={this.setSelected}
                      labelledBy="Select sites"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </FormGroup>

              <FormGroup>
                <Button
                  onClick={this.onSubmit}
                  className="loginbtn1 create-account-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <img
                      src={require("../../assets/images/button-loading.gif")}
                    />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </FormGroup>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default withTranslation()(CustomCSVExportModal);
